import React, { useMemo, useState } from "react";
import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";

import "./date-range-picker.scss";

const presets = [
  "Today",
  "Yesterday",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "Last 7 Days",
  "Last 30 Days",
  "Last 3 Months",
];

const getPresetRange = (preset) => {
  const today = new Date();

  switch (preset) {
    case "Today":
      return { startDate: today, endDate: today };
    case "Yesterday": {
      const yesterday = subDays(today, 1);
      return { startDate: yesterday, endDate: yesterday };
    }
    case "This Week":
      return {
        startDate: startOfWeek(today, { weekStartsOn: 1 }),
        endDate: endOfWeek(today, { weekStartsOn: 1 }),
      };
    case "Last Week": {
      const lastWeek = subDays(today, 7);
      return {
        startDate: startOfWeek(lastWeek, { weekStartsOn: 1 }),
        endDate: endOfWeek(lastWeek, { weekStartsOn: 1 }),
      };
    }
    case "This Month":
      return {
        startDate: startOfMonth(today),
        endDate: endOfMonth(today),
      };
    case "Last Month": {
      const lastMonth = subMonths(today, 1);
      return {
        startDate: startOfMonth(lastMonth),
        endDate: endOfMonth(lastMonth),
      };
    }
    case "Last 7 Days":
      return {
        startDate: subDays(today, 6),
        endDate: today,
      };
    case "Last 30 Days":
      return {
        startDate: subDays(today, 29),
        endDate: today,
      };
    case "Last 3 Months":
      return {
        startDate: subMonths(today, 3),
        endDate: today,
      };
    default:
      return { startDate: today, endDate: today };
  }
};

const DateRangePicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(value?.startDate || new Date());
  const [draft, setDraft] = useState(value || getPresetRange("Today"));
  const [selecting, setSelecting] = useState("start");

  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewDate), { weekStartsOn: 0 });
    const days = [];

    for (let i = 0; i < 42; i += 1) {
      days.push(addDays(start, i));
    }

    return days;
  }, [viewDate]);

  const applyPreset = (preset) => {
    const range = getPresetRange(preset);
    setDraft(range);
    setViewDate(range.startDate);
    setSelecting("start");
  };

  const handleDayClick = (day) => {
    if (selecting === "start") {
      setDraft({ startDate: day, endDate: day });
      setSelecting("end");
      return;
    }

    if (day < draft.startDate) {
      setDraft({ startDate: day, endDate: draft.startDate });
    } else {
      setDraft({ ...draft, endDate: day });
    }

    setSelecting("start");
  };

  const confirm = () => {
    onChange(draft);
    setOpen(false);
  };

  const label =
    value?.startDate && value?.endDate
      ? `${format(value.startDate, "dd MMM yyyy")} - ${format(value.endDate, "dd MMM yyyy")}`
      : "Today";

  return (
    <div className="date-range">
      <button
        type="button"
        className="date-range__trigger"
        onClick={() => setOpen(true)}
      >
        <span>{label}</span>
        <span><i className="ti ti-chevron-down" aria-hidden="true"></i></span>
      </button>

      {open && (
        <>
          <button
            type="button"
            className="date-range__backdrop"
            onClick={() => setOpen(false)}
          />

          <div className="date-range__modal">
            <div className="date-range__calendar">
              <div className="date-range__month-header">
                <button type="button" onClick={() => setViewDate(subMonths(viewDate, 1))}>
                  ‹
                </button>
                <h3>{format(viewDate, "MMMM yyyy")}</h3>
                <button type="button" onClick={() => setViewDate(addDays(endOfMonth(viewDate), 1))}>
                  ›
                </button>
              </div>

              <div className="date-range__weekdays">
                {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>

              <div className="date-range__days">
                {monthDays.map((day) => {
                  const isCurrentMonth = day.getMonth() === viewDate.getMonth();
                  const isStart = isSameDay(day, draft.startDate);
                  const isEnd = isSameDay(day, draft.endDate);
                  const inRange = isWithinInterval(day, {
                    start: draft.startDate,
                    end: draft.endDate,
                  });

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      className={[
                        "date-range__day",
                        !isCurrentMonth ? "date-range__day--muted" : "",
                        inRange ? "date-range__day--range" : "",
                        isStart || isEnd ? "date-range__day--selected" : "",
                      ].join(" ")}
                      onClick={() => handleDayClick(day)}
                    >
                      {format(day, "d")}
                    </button>
                  );
                })}
              </div>

              <div className="date-range__inputs">
                <div>
                  <label>Start Date</label>
                  <strong>{format(draft.startDate, "MM/dd/yyyy")}</strong>
                </div>
                <div>
                  <label>End Date</label>
                  <strong>{format(draft.endDate, "MM/dd/yyyy")}</strong>
                </div>
              </div>

              <div className="date-range__actions">
                <button type="button" onClick={() => setOpen(false)}>
                  Cancel
                </button>
                <button type="button" onClick={confirm}>
                  Confirm
                </button>
              </div>
            </div>

            <div className="date-range__presets">
              {presets.map((preset) => (
                <button key={preset} type="button" onClick={() => applyPreset(preset)}>
                  {preset}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DateRangePicker;